import { useEffect, useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { ReadingStepComponentProps } from "./types";
import { AudioCaptureRecorder } from "../../../../components/AudioCaptureRecorder";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import { customPalette } from "../../../../theme/palette";
import { ArrowDownward, ArrowForward } from "@mui/icons-material";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

export function OralReadingQuestion({
  assessment,
  isLoading,
  onStepReadyChange,
  onSaveAudio,
}: ReadingStepComponentProps) {
  const [isPassageVisible, setIsPassageVisible] = useState(false);
  const [hasCompletedRecording, setHasCompletedRecording] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const hasPromptContent = Boolean(assessment?.part?.prompt?.trim());
  const hasValidPartOrder = Boolean(assessment?.part?.partOrder);
  const canSkipStep = !hasPromptContent || !hasValidPartOrder;

  useEffect(() => {
    if (!isLoading && canSkipStep) {
      onStepReadyChange?.(true);
    }
  }, [canSkipStep, isLoading, onStepReadyChange]);

  if (isLoading) {
    return null;
  }

  if (canSkipStep || !assessment?.part) {
    return (
      <Typography>
        Reading passage unavailable. Continue to the next step when ready.
      </Typography>
    );
  }

  const partOrder = assessment?.part?.partOrder;
  const finishedText = "Nice work! We have your recording. Keep going!";

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "1 1 60%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
          p: isPassageVisible ? 3 : 0,
        }}
      >
        {isPassageVisible ? (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Typography variant={"body1"}>
              {assessment?.part?.prompt}
            </Typography>
          </Box>
        ) : (
          <Stack sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Box
              aria-hidden
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                opacity: 0.35,
              }}
            >
              {Array.from({ length: 10 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    height: 14,
                    bgcolor: customPalette.gray[300],
                    borderRadius: 1,
                    width: i % 3 === 2 ? "65%" : "100%",
                  }}
                />
              ))}
            </Box>
            <Stack
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box>
                <Typography
                  variant={"body1"}
                  sx={{ fontWeight: 800, color: customPalette.gray[800] }}
                >
                  Press the microphone{" "}
                </Typography>
                <Typography
                  variant={"body1"}
                  sx={{ fontWeight: 800, color: customPalette.gray[800] }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", md: "inline" } }}
                  >
                    <span style={{ textDecoration: "underline" }}>
                      on the right
                    </span>{" "}
                    to begin
                  </Box>
                  <Box
                    component="span"
                    sx={{ display: { xs: "inline", md: "none" } }}
                  >
                    <span style={{ textDecoration: "underline" }}>below</span>{" "}
                    to begin
                  </Box>
                </Typography>
              </Box>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={"center"}
                spacing={1}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    borderRadius: "50%",
                    height: 72,
                    width: 72,
                    bgcolor: customPalette.gray[400],
                  }}
                >
                  <MicRoundedIcon
                    sx={{
                      fontSize: 36,
                      color: customPalette.gray[0],
                    }}
                  />
                </Box>
                <ArrowForward
                  sx={{ fontSize: 35, display: { xs: "none", md: "block" } }}
                />
                <ArrowDownward
                  sx={{ fontSize: 35, display: { xs: "block", md: "none" } }}
                />
              </Stack>
            </Stack>
          </Stack>
        )}
      </Card>
      <Card
        sx={{
          flex: "1 1 40%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
          {hasCompletedRecording ? (
            <Stack>
              <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                {finishedText}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ display: "flex" }}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <InstructionAudioButton
                  audioUrl={assessment.oralReadingInstructionAudio}
                  label={`Play instruction audio`}
                  disabled={isRecording}
                ></InstructionAudioButton>
                <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
                  Instructions:
                </Typography>
              </Stack>
              <Box>
                <Typography variant={"body1"}>
                  <span style={{ fontWeight: 800 }}>Read</span> the text
                  outloud.
                </Typography>
              </Box>
              <Box>
                <Typography variant={"body1"}>
                  <span style={{ fontWeight: 800 }}>Press the microphone</span>{" "}
                  when you are ready to speak
                </Typography>
              </Box>
              <Box>
                <AudioCaptureRecorder
                  onRecordingChange={(recording) => {
                    setIsRecording(recording);
                    if (recording) {
                      setIsPassageVisible(true);
                    }
                  }}
                  onReadyChange={(isReady) => {
                    setHasCompletedRecording(isReady);
                  }}
                  onRecordingComplete={async ({
                    blob,
                    mimeType,
                    durationMs,
                  }) => {
                    await onSaveAudio?.({
                      entityType: "oral_reading_audio",
                      partOrder,
                      blob,
                      mimeType,
                      durationMs,
                    });
                    setHasCompletedRecording(true);
                    onStepReadyChange?.(true);
                  }}
                  showTimer={false}
                />
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
